import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import {
	Activity,
	ActivityCategory,
	ActivityCategoryId,
	ActivityCollaborationInvitation,
	ActivityCollaborationOriginal,
	ActivityDateType,
	ActivityFilterSpecification,
	ActivityParent,
	ActivityParentType,
	ActivityService,
	ActivitySubType,
	ActivityType,
	ActivityTypeCollection,
	ActivityTypeId,
	AuthenticatedIdentity,
	AuthenticationService,
	CurrentUser,
	DossierService,
	HumanResources,
	IdAndName,
	Option,
	OutReachTypeLabels,
	OutReachTypes,
	Project,
	ProjectService,
	Theme,
	UserPreferenceService
} from '../../domain';
import { ActivityListComponent } from '../../widgets/activity-list/activity-list.component';
import { ConsoleService } from '../../widgets/console.service';
import { TooltipService } from '../../widgets/tooltip.service';
import { ActivityCollaborationConfirmationModalComponent, ActivityCollaborationConfirmationModalResponse } from '../activity-collaboration-confirmation-modal/activity-collaboration-confirmation-modal.component';
import { ActivityModalService } from '../activity-modal.service';
import { GoogleAnalyticsService } from '../google-analytics.service';
import * as Search from './../../widgets/search';

enum ActivityListState {
	All,
	UpcomingAndRecent,
	Ongoing
}

@Component({
  selector: 'app-activities',
  templateUrl: './activities.component.html',
  styleUrls: ['./activities.component.scss']
})
export class ActivitiesComponent implements OnInit {
	@Input() tabsVisible = true;
	@Input() parent: ActivityParent;
	@Input() copyActivity: Event;

	private _activities: Observable<Activity[]>;
	private _filteredActivities: Observable<Activity[]>;
	private searchInput = new FormControl();
	private readonly defaultFilterFormValues = {
			activeTabId: null,
			withNameMatching: null,
			startDate: null,
			endDate: null,
			combinedActivityTypes: null,
			belongingToAThemeWithIdIn: null,
			belongingToAProjectWithIdIn: null,
			missingInformation: null,
			clienteleContactsType: null,
			clienteleGroup: null
	};

	filterForm: FormGroup;

	ActivityParentType = ActivityParentType;
	ActivityTypeId = ActivityTypeId;
	activitiesByTabId: {};
	filteredActivitiesByTabId: {};
	tabs: ActivityCategory[];
	activeTab: ActivityCategory;
	activityTypes: ActivityTypeCollection = new ActivityTypeCollection([]);
	availableActivityTypes: ActivityType[];
	collaborationInvitations: ActivityCollaborationInvitation[] = [];
	themes: Theme[] = undefined;
	projects: Project[] = undefined;
	currentUser: CurrentUser;
	OutreachTypes = OutReachTypes;
	@ViewChild(ActivityListComponent ) activityList: ActivityListComponent ;
	constructor(
		private activityService: ActivityService,
		private matSnackbar: MatSnackBar,
		private matDialog: MatDialog,
		private userPreferenceService: UserPreferenceService,
		private console: ConsoleService,
		private activityModals: ActivityModalService,
		private themeService: DossierService,
		private projectService: ProjectService,
		fb: FormBuilder,
		authService: AuthenticationService,
		private gaService: GoogleAnalyticsService,
		public tooltipSvc: TooltipService
	){
		this.searchInput
			.valueChanges
			.pipe(debounceTime(200))
			.subscribe(e => this.searchTerm = e);

		this.activityTypes = this.activityService.getActivityTypes();
		
		this.availableActivityTypes = this.activityTypes.all;

		this.filterForm = fb.group(this.defaultFilterFormValues);

		this.tabs = 
			ActivityCategory.createForAcademicUserOption(
				this.activityTypes,
				authService.tryGetCurrentAcademicUserSync());

		this.activateTab(this.tabs[0]);

		this.filterForm.valueChanges
			.pipe(debounceTime(200))
			.forEach(() => {
				if (this.filterForm.status === 'INVALID') {
					this.console.error('Filter Form errors:', this.filterForm.errors);
					return;
				}
				this.activityList?.resetBulkUpdate();
				/* StartDate/EndDate could be the reason for the form being in an invalid state.
				** To prevent an exception, we only attempt to resolve the new activity value
				** in the case of a *valid* form state. **/
				var activityFilter = this.getActivityFilterFromForm();
				this.console.info('Activity Filter Changed: form -> activity:', this.filterForm.value, activityFilter);
				this.gaService.eventEmitter("Activity Filter Search", "Activity Filter", "Search");
				this.activityService.searchActivities(activityFilter);
			});

		authService.currentIdentity.subscribe(identity => {
			identity.doWithValue((i: AuthenticatedIdentity) => {
				this.currentUser = i.user;
				this.availableActivityTypes = this.activityTypes.getFilteredByUser(i.user).all;
			});
		});
	}

	hasInstructions() {
		if (this.getInstructions().length > 0) {
			return true;
		} 
		return false;
	}

	getInstructions(): string {
		return this.tooltipSvc.tooltip("ActivityInstructions." 
			+ this.tooltipSvc.mapMeritAndPromotionsHRToString(this.currentUser.roles.academic.map(a => a.meritAndPromotionHr).getValueOrDefault(HumanResources.ANR)));
	}

	searchTerm = '';
	search = Search.ResultsFunction.fromPredicate(
		Search.Predicate.objectStringProjectors<Activity>(
		[
			a => this.activityTypes.typeName(a),
			a => a.name,
			a => a.tags.map(t => t.name).join(''),
			a => a.parent.match(() => '', v => v.name)
		]));

	states = [
		{
			id: ActivityListState[ActivityListState.Ongoing],
			label: 'Ongoing'
		},
		{
			id: ActivityListState[ActivityListState.UpcomingAndRecent],
			label: 'Upcoming and Recent'
		},
		{
			id: ActivityListState[ActivityListState.All],
			label: 'All'
		}
	];

	viewingActivity: Activity | null = null;

	get activities(): Activity[] {
		return (!!this.activitiesByTabId && this.activeTab.id in this.activitiesByTabId)
			? this.activitiesByTabId[this.activeTab.id]
			: undefined;
	}
	
	get filteredActivities(): Activity[] {
		return (!!this.filteredActivitiesByTabId && this.activeTab.id in this.filteredActivitiesByTabId)
			? this.filteredActivitiesByTabId[this.activeTab.id]
			: undefined;
	}

	private loadCollaborationInvitations(){
		this.activityService.getCollaborationInvitations().then(xs => this.collaborationInvitations = xs);
	}

	private readonly today = new Date();
	get activitiesRecent(){
		let acts = this.activities;
		if(!acts){
			return;
		}
		return acts.filter(a => 
			a.date.match(
				single => single.date < this.today,
				range => range.startDate < this.today && range.endDate < this.today,
				ongoing => ongoing.startDate < this.today
			));
	}
	get activitiesUpcoming(){
		let acts = this.activities;
		if(!acts){
			return;
		}
		return acts.filter(a => 
			a.date.match(
				single => single.date >= this.today,
				range => range.startDate >= this.today || range.endDate >= this.today,
				ongoing => ongoing.startDate >= this.today
			));
	}
	get activitiesOngoing(){
		let acts = this.activities;
		if(!acts){
			return;
		}
		return acts.filter(a => 
			a.date.typeId == ActivityDateType.Ongoing);
	}
	private _currentStateId = ActivityListState.All;
	get currentStateId(){
		return ActivityListState[this._currentStateId];
	}

	/**
	 * Return key value pairs for OutreachTypes enum to be used in the outreach type dropdown in the activity filter.
	 */
  public readonly clienteleContactTypes = Object.values(OutReachTypes).map(id => ({ id, name: OutReachTypeLabels[id] }));

	get showsAudienceFilter(): boolean {
  	return [ActivityCategoryId.ExtensionEffortResearchCreativeCategory,
          ActivityCategoryId.AcademicCoordinationCategory].includes(this.activeTab.id);
	}

	activateTab(tab: ActivityCategory){
		if(this.activeTab === tab){
			return;
		}
		this.activeTab = tab;
		this.filterForm.patchValue({ activeTabId: tab.id });
	}

	switchToStateId(stateId: string){
		this._currentStateId = ActivityListState[stateId];
	}

    ngOnInit() {
		// subscribe to activities in service
		this._activities = !!this.parent 
			? this.activityService.getActivitiesByParent(this.parent) 
			: this.activityService.activities;
		this._filteredActivities = this.activityService.filteredActivities;

		// does http request to get all activities
		this.activityService.loadActivities();

		this.themeService.getThemes().then(themes => {
			this.themes = themes;
		});

		this.projectService.projects.subscribe(p => {
			this.projects = p;
		});

		this.projectService.loadProjects();

		if(!this.parent){
			this.loadCollaborationInvitations();
		}

		this.subscribeForActivityTabs(this._activities).subscribe(byTabId => {this.activitiesByTabId = byTabId; });
		this.subscribeForActivityTabs(this._filteredActivities).subscribe(byTabId => {this.filteredActivitiesByTabId = byTabId; });

		this.console.log('activityTypes (O)',this.activityTypes);
    }

	private subscribeForActivityTabs(activities: Observable<Activity[]>): Observable<{}> {
		return activities.pipe(map(acts => {
			//this.activities = acts;
			let byTabId = {};
			let tabByActivitySubTypeId = {};
			this.tabs.forEach(t => {
				byTabId[t.id] = [];
				t.activityTypes.all.forEach(at => 
						at.subTypes.forEach(st =>
							tabByActivitySubTypeId[st.id] = t));
			});
			this.console.log('tabByActivitySubTypeId (O):',tabByActivitySubTypeId),
			acts.forEach(a => {
				if(!(a.subTypeId in tabByActivitySubTypeId)){
					return;
				}
				byTabId[tabByActivitySubTypeId[a.subTypeId].id].push(a);
			});

			// opens an activity in a modal after collaboration confirmation and the user selected the checkbox to edit the new activity.
			if(!!this.editCollaborationActivityId) {
				let activity = acts.find(a => 
					a.activityCollaborationOriginal
					.getValueOrDefault(new ActivityCollaborationOriginal(0, new IdAndName(0, ""), ""))
					.id == this.editCollaborationActivityId);
				if(!!activity){
					this.openActivityInModal(activity);
					this.editCollaborationActivityId = null;
				}
			}
			this.console.log('activities loaded (O):', acts, this.activitiesByTabId);
			return byTabId;
		}));
	}

	openActivityMenu(){
		this.console.log('current activity category id: ', this.activeTab.id);
		this.activityModals.openActivityMenu(
			this.matDialog,
			Option.create(this.parent),
			this.activeTab.id);
	}

	editActivity(activity: Activity){
		if (this.userPreferenceService.getOpenActivityInModalPreference()){
			this.openActivityInModal(activity);
		} else {
			// opens sidebar for editing
			this.viewingActivity = activity;
		}
	}

	private editCollaborationActivityId: number = null;
	add(collab: ActivityCollaborationInvitation){
		ActivityCollaborationConfirmationModalComponent.open(this.matDialog, collab).afterClosed().subscribe((result: ActivityCollaborationConfirmationModalResponse) =>
		{
			this.console.log("acivity collaboration result: ", result);
			if(!result.confirmed){
				return;
			}
			if(result.editAfterSaving){
				this.editCollaborationActivityId = result.originalActivityId;
				//this.console.log("editCollaborationActivityId:", this.editCollaborationActivityId);
				this.activityService.loadActivities();
			}
			this.loadCollaborationInvitations();
			
		});
	}

	ignore(collab: ActivityCollaborationInvitation){
		this.activityService.ignoreCollaboration(collab.originalActivity.id).then(result => 
			{
				result.matchDo(
					_ => this.matSnackbar.open("Collaboration invitation ignored"),
					msg => this.matSnackbar.open("Unable to ignore collaboration invitation: " + msg));
					this.loadCollaborationInvitations();
			});
	}

	openActivityInModal(activity: Activity){
		this.activityModals.openActivityInModal(this.matDialog, activity);
	}

	startNewActivity(subType: ActivitySubType){
		this.activityModals.startNewActivity(
			this.matDialog, 
			subType, 
			Option.create<ActivityParent>(this.parent));
	}

	startActivityCopy(activity: Activity){
		this.activityModals.startActivityCopy(this.matDialog, activity);
	}

	resetFilters() {
		this.filterForm.setValue(this.defaultFilterFormValues);
		this.gaService.eventEmitter("Activity Filter Reset", "Activity Filter", "Reset");
    this.activityList.resetBulkUpdate();
	}

	getActivityFilterFromForm() {
		let fv = this.filterForm.value;
		
		let activityTypesSelected = (<string[]>fv.combinedActivityTypes || [])
			.filter(s => s.substr(0,3) == "at_")
			.map(s => +(s.replace("at_", "")));
		let activitySubTypesSelected = (<string[]>fv.combinedActivityTypes || [])
			.filter(s => s.substr(0,3) == "st_")
			.map(s => +s.replace("st_", ""));

		// Audience Attendance (clientele contact type / group) only applies to tabs
    // that surface those controls; ignore stale values carried over from another tab.
    let audienceApplies = this.showsAudienceFilter;


		return new ActivityFilterSpecification(
			fv.withNameMatching,
			activityTypesSelected,
			activitySubTypesSelected,
			<Date>fv.startDate,
			<Date>fv.endDate,
			fv.belongingToAThemeWithIdIn,
			fv.belongingToAProjectWithIdIn,
			fv.missingInformation,
      audienceApplies ? fv.clienteleContactsType : null,
      audienceApplies ? fv.clienteleGroup?.id : undefined
		); 
	}

	dateChange(type: string, e: MatDatepickerInputEvent<Date>){
		this.console.info('Date Change: (' + type + ')', e.value);
	}
}